import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PCCARE_LOGO from "../../assests/images/Bgpccare.png";
import { getProductEnquiryLink } from "../../utils/whatsapp";
import { getRequest } from "../../apis/ApiInstence";

const BOOKING_CHARGE = 1000;
const TRUST_POINTS = [
  "Quality-checked before dispatch",
  "Secure packaging for safe delivery",
  "Warranty & return support as per policy",
  "Quick WhatsApp support for any help",
];

const isBlank = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === "string" && value.trim() === "");

const getBackendRoot = () => {
  const raw =
    process.env.REACT_APP_BACKEND ||
    process.env.REACT_APP_BACKEND_URL ||
    process.env.REACT_APP_API_BASE_URL ||
    "http://localhost:8000";
  const trimmed = String(raw).replace(/\/$/, "");
  return trimmed.replace(/\/api\/v1$/i, "");
};

const parseImages = (rawImages) => {
  if (!rawImages) return [];
  if (Array.isArray(rawImages)) return rawImages.filter(Boolean).map(String);
  if (typeof rawImages === "string") {
    const value = rawImages.trim();
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean).map(String);
    } catch {
      // ignore
    }
    return value
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
};

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryType = new URLSearchParams(location.search).get("type") || "product";

  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeImg, setActiveImg] = useState(0);
  const [productReviews, setProductReviews] = useState([]);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [openImages, setOpenImages] = useState({});
  const [expandedReviews, setExpandedReviews] = useState({});

  /* ================= FETCH PRODUCT OR BANNER ================= */
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        if (queryType === "banner") {
          const bannerRes = await getRequest("/banner");
          const banners = bannerRes?.data;
          const banner = Array.isArray(banners)
            ? banners.find((b) => String(b.id) === String(id))
            : null;
          if (banner && banner.status && String(banner.status) !== "active") {
            setProduct(null);
            setLoadError("This item is not available.");
          } else {
            setProduct(banner || null);
            if (!banner) setLoadError("Item not found.");
          }
          return;
        }

        let data;
        try {
          const res = await getRequest(`/products/one/${id}`);
          data = res?.data;
        } catch (error) {
          if (error?.response?.status !== 404) throw error;
          const res = await getRequest(`/products/${id}`);
          data = res?.data;
        }

        const fetched = data?.product || data || null;
        if (fetched && fetched.status && String(fetched.status) !== "active") {
          setProduct(null);
          setLoadError("This product is currently unavailable.");
          return;
        }

        setProduct(fetched);
        if (!fetched) setLoadError("Product not found.");
      } catch (error) {
        console.error("Error fetching product:", error);
        setProduct(null);
        setLoadError(
          error?.response?.data?.message ||
            error?.message ||
            "Failed to load product details."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, queryType]);

  useEffect(() => {
    setActiveImg(0);
  }, [id, queryType]);

  useEffect(() => {
    if (!id || queryType === "banner") return;
    let ignore = false;
    const fallbackLocal = () => {
      try {
        const stored = JSON.parse(localStorage.getItem(`product_review_${id}`));
        if (Array.isArray(stored)) return stored;
        if (stored && typeof stored === "object") return [stored];
      } catch {
        // ignore
      }
      return [];
    };
    const normalizeReviews = (list) =>
      list.map((item) => ({
        id: item?.id || item?.review_id || null,
        product_id: item?.product_id || id,
        name: item?.name || "Customer",
        rating: Number(item?.rating || 0),
        description: item?.message ?? item?.description ?? "",
        image: item?.image || item?.imagePreviewUrl || "",
        updatedAt: item?.updated_at || item?.updatedAt || item?.created_at || item?.createdAt,
      }));

    const loadReviews = async () => {
      setReviewLoading(true);
      setReviewError("");
      try {
        let res;
        try {
          res = await getRequest(`reviews?product_id=${encodeURIComponent(id)}&status=active`);
        } catch (err) {
          if (err?.response?.status === 404) {
            res = await getRequest(`review?product_id=${encodeURIComponent(id)}&status=active`);
          } else {
            throw err;
          }
        }
        const list = Array.isArray(res?.data) ? res.data : [];
        const normalized = normalizeReviews(list);
        if (!ignore) setProductReviews(normalized);
      } catch (err) {
        const local = normalizeReviews(fallbackLocal());
        if (!ignore) {
          setProductReviews(local);
          setReviewError(
            err?.response?.data?.message ||
              err?.message ||
              "Unable to load reviews right now."
          );
        }
      } finally {
        if (!ignore) setReviewLoading(false);
      }
    };
    loadReviews();
    return () => {
      ignore = true;
    };
  }, [id, queryType]);

  const backendRoot = useMemo(() => getBackendRoot(), []);
  const uploadsBase = `${backendRoot}/uploads`;

  const imageArray = useMemo(() => {
    if (queryType === "banner") return [];
    return parseImages(product?.images);
  }, [product?.images, queryType]);

  const bannerImage = useMemo(() => {
    if (queryType !== "banner") return "";
    const raw = product?.image ? String(product.image).replace(/^\/+/, "") : "";
    return raw ? `${backendRoot}/banner/${raw}` : "";
  }, [backendRoot, product?.image, queryType]);

  const resolveReviewImage = (value) => {
    if (!value) return "";
    const raw = String(value);
    if (/^https?:\/\//i.test(raw)) return raw;
    return `${backendRoot}/uploads/${raw.replace(/^\/+/, "")}`;
  };

  const formatReviewDate = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  const renderStars = (ratingValue) => {
    const rating = Math.max(0, Math.min(5, Number(ratingValue) || 0));
    const fullStars = Math.round(rating);
    return (
      <div className="flex items-center gap-1 text-sm text-amber-500">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={`star-${star}`}>
            {star <= fullStars ? "★" : "☆"}
          </span>
        ))}
        <span className="ml-1 text-xs text-slate-500">{rating.toFixed(1)}</span>
      </div>
    );
  };

  const toggleImage = (key) => {
    setOpenImages((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleReview = (key) => {
    setExpandedReviews((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const isLongDescription = (text) => {
    if (!text) return false;
    return String(text).length > 120;
  };

  const clampStyle = {
    display: "-webkit-box",
    WebkitLineClamp: 3,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };

  const getInitials = (name) => {
    const safe = String(name || "").trim();
    if (!safe) return "U";
    const parts = safe.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
    return (first + last).toUpperCase() || "U";
  };

  const getAvatarColor = (name) => {
    const palette = ["bg-indigo-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-sky-500"];
    const safe = String(name || "");
    let hash = 0;
    for (let i = 0; i < safe.length; i += 1) {
      hash = safe.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % palette.length;
    return palette[index];
  };

  const safeActiveImg = Math.min(
    Math.max(activeImg, 0),
    Math.max(imageArray.length - 1, 0)
  );

  const specRows = useMemo(() => {
    const rows = [];
    if (!product) return rows;

    const addRow = (label, value) => {
      if (isBlank(value)) return;
      rows.push({ label, value: String(value) });
    };

    addRow("Brand", product.brand);
    addRow("Processor", product.processor);
    addRow("Generation", product.generation);
    addRow("RAM", product.ram);
    addRow("SSD", product.ssd);
    addRow("Screen Size", product.screen_size);
    if (!isBlank(product.weight)) addRow("Weight", product.weight);
    if (!isBlank(product.stock_quantity)) addRow("Stock Qty", product.stock_quantity);

    if (queryType === "banner") {
      addRow("Storage", product.storage);
      addRow("Battery Life", product.battery_life);
      addRow("GPU", product.gpu);
    }

    return rows;
  }, [product, queryType]);

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto py-20 text-center">
        <h2 className="text-2xl font-bold">Loading...</h2>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-4xl mx-auto py-20 text-center">
        <h2 className="text-2xl font-bold">Product not found</h2>
        {loadError && <p className="mt-2 text-sm text-gray-600">{loadError}</p>}
        <button
          type="button"
          onClick={() => navigate("/products")}
          className="mt-6 inline-flex items-center justify-center rounded-xl border border-gray-300 px-5 py-2 text-sm font-semibold hover:bg-gray-50"
        >
          Back to Products
        </button>
      </div>
    );
  }

  const handleBookNow = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];

    const cartItem =
      queryType === "banner"
        ? {
            ...product,
            item_type: "banner",
            quantity: 1,
          }
        : {
            ...product,
            item_type: "product",
            quantity: 1,
          };

    const existingIndex = cart.findIndex((i) => i.id === cartItem.id);
    let updated = cart;
    if (existingIndex === -1) {
      updated = [...cart, cartItem];
    } else {
      updated = [...cart];
      updated[existingIndex] = {
        ...updated[existingIndex],
        item_type: updated[existingIndex].item_type || cartItem.item_type,
        quantity: updated[existingIndex].quantity ?? cartItem.quantity
      };
    }

    localStorage.setItem("cart", JSON.stringify(updated));
    window.dispatchEvent(new Event("cartUpdated"));
    navigate("/cart");
  };

  const newPrice = Number(product.selling_price || 0);
  const oldPrice = Number(product.original_price || 0);
  const showOldPrice = oldPrice > 0 && oldPrice > newPrice;

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">

      {/* TOP BAR */}
      <div className="flex items-center gap-3 text-gray-500 text-sm mb-8">
        <button
          onClick={() => navigate("/")}
          className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 hover:bg-gray-100"
        >
          <i className="fa-solid fa-chevron-left"></i>
        </button>
        <span>Home</span>
        <span>/</span>
        <button
          onClick={() => navigate("/products")}
          className="hover:underline"
        >
          Products
        </button>
        <span>/</span>
        <span className="text-black font-medium line-clamp-1">
          {product.name}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

        {/* IMAGE SECTION */}
        <div>
          <div className="bg-white rounded-2xl p-6 border flex justify-center">
            <img
              src={
                queryType === "banner"
                  ? bannerImage || PCCARE_LOGO
                  : imageArray.length > 0
                    ? `${uploadsBase}/${encodeURIComponent(imageArray[safeActiveImg])}`
                    : PCCARE_LOGO
              }
              alt={product.name}
              className="max-h-[420px] object-contain"
            />
          </div>

          {queryType !== "banner" && imageArray.length > 1 && (
            <div className="flex gap-4 mt-4">
              {imageArray.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImg(idx)}
                  className={`w-20 h-20 border rounded-xl flex justify-center ${
                    activeImg === idx
                      ? "border-[#1F3C88]"
                      : "border-gray-300"
                  }`}
                >
                  <img
                    src={`${uploadsBase}/${encodeURIComponent(img)}`}
                    alt="thumb"
                    className="max-h-full object-contain"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO SECTION */}
        <div>
          <img
            src={PCCARE_LOGO}
            alt="PC Care"
            className="w-12 mb-3"
          />

          <h1 className="text-2xl font-bold text-[#1F3C88]">
            {product.name}
          </h1>

          {product.description && (
            <p className="text-gray-600 mt-3">{product.description}</p>
          )}

          <>
            <div className="mt-4">
              <span className="text-3xl font-extrabold text-[#E63946]">
                ₹{newPrice.toLocaleString()}
              </span>
              {showOldPrice && (
                <span className="line-through text-gray-400 ml-3">
                  ₹{oldPrice.toLocaleString()}
                </span>
              )}
            </div>

            <p className="text-sm mt-2">
              Booking Charge: ₹{BOOKING_CHARGE}
            </p>
          </>

          {specRows.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold text-[#1F3C88] mb-3">
                DETAILS
              </h4>
              <div className="space-y-2 text-sm text-gray-700">
                {specRows.map((row) => (
                  <div key={row.label} className="flex justify-between gap-6">
                    <span className="text-gray-600">{row.label}</span>
                    <span className="font-medium text-gray-900 text-right">
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* <div className="mt-5 rounded-xl bg-slate-50 p-4">
            <h4 className="text-sm font-semibold text-[#1F3C88] mb-2">
              Why PC Care?
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              {TRUST_POINTS.map((point, i) => (
                <li key={i}>• {point}</li>
              ))}
            </ul>
          </div> */}



          <div className="mt-8 flex gap-4">
            <button
              onClick={handleBookNow}
              className="w-1/2 py-3 rounded-xl font-semibold bg-gradient-to-r from-[#1F3C88] to-[#3f5bdc] text-white"
            >
              {`Buy Now `}
            </button>

         <a
  href={getProductEnquiryLink(
    { ...product, id },
    { type: queryType }
  )}
  target="_blank"
  rel="noopener noreferrer"
  className="w-1/2 py-3 rounded-xl font-semibold border-2 border-[#1F3C88] text-[#1F3C88] hover:bg-[#1F3C88] hover:text-white transition flex items-center justify-center"
>
  Enquiry Now
</a>
          </div>
        </div>
      </div>

      {/* REVIEWS */}
      {queryType !== "banner" && (
        <div className="mt-10  ">
          <h3 className="text-[30px] underline font-bold text-[#1F3C88]">Reviews:</h3>
          {reviewLoading ? (
            <p className="mt-3 text-sm text-gray-500">Loading reviews...</p>
          ) : productReviews.length === 0 ? (
            <p className="mt-3 text-sm text-gray-500">No reviews yet for this product.</p>
          ) : (
            <div className="mt-4 flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              {productReviews.map((review, idx) => (
                <div
                  key={`${review?.product_id || id}-${idx}`}
                  className="min-w-[260px] max-w-[320px] flex-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white ${getAvatarColor(
                          review?.name
                        )}`}
                      >
                        {getInitials(review?.name || "Customer")}
                      </div>
                      <p className="text-sm font-semibold text-slate-800">
                        {review?.name || "Customer"}
                      </p>
                    </div>
                    <p className="text-xs text-slate-500">
                      {formatReviewDate(review?.updatedAt)}
                    </p>
                  </div>
                  <div className="mt-2">
                    {renderStars(review?.rating || 0)}
                  </div>
                  {review?.description && (() => {
                    const key = `${review?.product_id || id}-${idx}`;
                    const expanded = Boolean(expandedReviews[key]);
                    const long = isLongDescription(review.description);
                    return (
                      <div className="mt-2">
                        <p
                          className="text-sm text-slate-600"
                          style={!expanded ? clampStyle : undefined}
                        >
                          {review.description}
                        </p>
                        {long && (
                          <button
                            type="button"
                            onClick={() => toggleReview(key)}
                            className="mt-1 text-xs font-semibold text-[#1F3C88]"
                          >
                            {expanded ? "See less" : "See more"}
                          </button>
                        )}
                      </div>
                    );
                  })()}
                  {(review?.image || review?.imagePreviewUrl) && (() => {
                    const key = `${review?.product_id || id}-${idx}`;
                    const open = Boolean(openImages[key]);
                    return (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={() => toggleImage(key)}
                          className="flex items-center gap-2 text-xs font-semibold text-[#1F3C88]"
                        >
                          <span>{open ? "Hide image" : "View image"}</span>
                          <span>{open ? "▴" : "▾"}</span>
                        </button>
                        {open && (
                          <img
                            src={resolveReviewImage(review.image || review.imagePreviewUrl)}
                            alt="Review"
                            className="mt-2 h-20 w-20 rounded-lg border border-slate-200 object-cover"
                          />
                        )}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          )}
          {reviewError && (
            <p className="mt-3 text-xs text-red-500">{reviewError}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
